import React, { useRef } from 'react'
import styled from 'styled-components'
import BackLink from 'components/BackLink'
import { ink, line, radius, surface, font, bp as bpToken } from 'features/dashboard/tokens'
import PassportDetailSkeleton from 'components/PassportDetailSkeleton'
import TabNav from 'components/TabNav'
import { keepStripPinned } from 'components/TabNav/keepStripPinned'

const DetailShell = ({
  backLabel,
  onBack,
  title,
  subtitle,
  headerActions,
  headerExtra,
  headerCard,
  tabs = [],
  activeTab,
  onTabChange,
  accent = '#33becc',
  loading = false,
  notFound = false,
  children,
}) => {
  const tabsRef = useRef(null)
  const selectTab = key => keepStripPinned(tabsRef.current, () => onTabChange && onTabChange(key))

  return (
    <Page>
      {(backLabel || onBack) && (
        <BackLink onClick={onBack}>{backLabel}</BackLink>
      )}

      {loading ? (
        <PassportDetailSkeleton />
      ) : notFound ? (
        <NotFound>This could not be found.</NotFound>
      ) : (
        <>
          {headerCard ? (
            <Header>
              {headerCard}
              {headerExtra}
            </Header>
          ) : (
          <Header>
            <HeaderTop>
              <Titles>
                {title && <Title>{title}</Title>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
              </Titles>
              {headerActions && <HeaderActions>{headerActions}</HeaderActions>}
            </HeaderTop>
            {headerExtra}
          </Header>
          )}

          <ContentCard>
            {tabs.length > 0 && (
              <TabsWrap ref={tabsRef} data-tour='passport-tabs' data-tour-id='passport.tabs'>
                <TabNav items={tabs} activeKey={activeTab} onSelect={selectTab} accent={accent} />
              </TabsWrap>
            )}
            <Content>{children}</Content>
          </ContentCard>
        </>
      )}
    </Page>
  )
}

export default DetailShell

const Page = styled.div`
  width: 100%;
  padding: 20px 24px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${bpToken.mobile}) {
    padding: 16px 6px;
  }
`


const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const Titles = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h1`
  margin: 0;
  font-size: ${font.title};
  font-weight: 700;
  color: ${ink.base};
  line-height: 1.2;
`

const Subtitle = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const HeaderActions = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`






const NotFound = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`

const ContentCard = styled.div`
  box-sizing: border-box;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: 0 1px 2px rgba(12, 18, 41, 0.04);
  padding: 4px 20px 20px;
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    padding: 4px 10px 16px;
  }
`

const TabsWrap = styled.div`
  position: sticky;
  top: var(--passport-bar-bottom, 0px);
  z-index: 30;
  margin: 0 -20px;
  padding: 0 20px;
  background: ${surface.card};

  @media (max-width: ${bpToken.mobile}) {
    margin: 0 -10px;
    padding: 0 10px;
  }
`

const Content = styled.div`
  min-width: 0;
  min-height: calc(100vh - 160px);
  margin-top: 16px;
`
